import { TControlName, TUpdaterName } from '@infotech-soe/metaschema';
import { ConnectedProps, legacy_connect as connect } from 'react-redux';

import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../../store/reducers/root';
import { getFormDataset } from '../../../store/selectors/dataset';
import {
  getAreHintsShown,
  getFormButtons,
  getFormClear,
  getFormControlNames,
  getFormHeight,
  getFormSourceType,
  getFormTitle,
  getFormWidth,
  getHasHints,
  getRootFormControlId,
  isFormReadonly,
  isSubmitting,
  isSwitchDisabled,
  submitForbidden,
} from '../../../store/selectors/form';
import dataset, { DatasetSwitchEffect } from '../../../store/slices/dataset';
import form, { FormCloseReasons } from '../../../store/slices/form';

import FormDialog from './FormDialog';

interface Props {
  open: boolean;
  id: UniqueId;
}

const mapStateToProps = (state: AppState, { id }: Props) => ({
  hasHints: getHasHints(state, id),
  areHintsShown: getAreHintsShown(state, id),
  controlNames: getFormControlNames(state, id),
  rootControlId: getRootFormControlId(state, id),
  title: getFormTitle(state, id),
  submitForbidden: submitForbidden(state, id),
  isSubmitting: isSubmitting(state, id),
  formSourceType: getFormSourceType(state, id),
  readonly: isFormReadonly(state, id),
  dataset: getFormDataset(state, id),
  disabledNext: isSwitchDisabled(state, id).next,
  disabledPrev: isSwitchDisabled(state, id).prev,
  formClear: getFormClear(state, id),
  width: getFormWidth(state, id),
  height: getFormHeight(state, id),
  formButtons: getFormButtons(state, id),
});

const mapDispatchToProps = {
  onClear: form.clear,
  onBtnSave: form.submitting,
  onSubmitted: form.submitted,
  onSubmit: form.submit,
  onCancel: form.close,
  selectNextRecord: dataset.selectNextRecord,
  selectPreviousRecord: dataset.selectPreviousRecord,
  onToggleHintClick: form.toggleHints,
  onButtonClick: form.runSingleUpdater,
};

const mergeProps = (
  msP: ReturnType<typeof mapStateToProps>,
  {
    onClear,
    onBtnSave,
    onSubmitted,
    onSubmit,
    onCancel,
    selectNextRecord,
    selectPreviousRecord,
    onToggleHintClick,
    onButtonClick,
  }: typeof mapDispatchToProps,
  { id: formId, ...oP }: Props
) => ({
  ...msP,
  ...oP,
  id: formId,
  onClear: () => onClear({ formId }),
  onBtnSave: () => onBtnSave({ formId }),
  onSubmitted: () => onSubmitted({ formId }),
  onSubmit: () => onSubmit({ formId }),
  onCancel: () => onCancel({ formId, reason: FormCloseReasons.CANCEL }),
  selectNext: () =>
    selectNextRecord({
      datasetId: msP.dataset ?? '',
      effect: DatasetSwitchEffect.REVIEW_FORM,
    }),
  selectPrev: () =>
    selectPreviousRecord({
      datasetId: msP.dataset ?? '',
      effect: DatasetSwitchEffect.REVIEW_FORM,
    }),
  onToggleHintClick: () => onToggleHintClick({ formId }),
  onButtonClick: (field: TControlName, name: TUpdaterName) =>
    onButtonClick({ formId, field, name }),
});

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export type TFormDialogConnectorProps = ConnectedProps<typeof connector>;

export default connector(FormDialog);
